import { FILESTORE_URL } from '@/settings';
import { store } from '@/store';
import { onAddMediaFile_Cn_Doc } from '@/store/actions';
import { CDSClient } from '@/sync/cds';
import { zwClient } from '@/sync/zapworks';
import { IFsFileData, IFsFileType } from '@r3f-component-data-structure';
import { IFileType } from '@zappar/zw-api-client';
import { FsAudio, FsExternalFile, FsFile, FsImage, FsImage360, FsImageTarget, FsVideo, FsVideo360, zmlToExternalFile } from './files';
import { FsModel3D } from './files/model3d';

/** Filestore object key (relative to `proj-<id>/<fileId>/`) that each media type is stored under. */
const PROJECT_FILE_KEY_BY_TYPE: Partial<Record<IFsFileType, string>> = {
    [IFsFileType.Image]: 'img',
    [IFsFileType.Video]: 'video.mp4',
    [IFsFileType.Model3d]: 'model.glb',
    [IFsFileType.Audio]: 'aud.mp3',
};

/**
 * Content type to store each object under. S3 keeps whatever the PUT declares and CloudFront serves
 * it back verbatim, so an object uploaded without one is stored as `binary/octet-stream` and a
 * `<video>`/`<audio>` element then refuses to play it. `img` is the exception: it holds whatever
 * image format the source gave us, so the file's own type is used and this map has no entry.
 */
const CONTENT_TYPE_BY_KEY: { [key: string]: string } = {
    'video.mp4': 'video/mp4',
    'model.glb': 'model/gltf-binary',
    'aud.mp3': 'audio/mpeg',
    'frame0.png': 'image/png'
};

/**
 * Mirrors the CDS's own `isPublicFsType`: video is streamed rather than bundled into the published
 * experience, so it has to be readable without a filestore JWT. The CloudFront viewer function
 * grants unauthenticated GET/HEAD purely on the `public-` prefix, so the prefix has to be minted
 * into the file id at creation time — it can't be added later without moving the objects.
 */
const isPublicFsType = (type: IFsFileType) => type === IFsFileType.Video || type === IFsFileType.Video360

/** Mints a filestore file id, carrying the `public-` prefix for the types that need one. */
export const newFsFileId = (type: IFsFileType) => `${isPublicFsType(type) ? 'public-' : ''}${crypto.randomUUID()}`

/**
 * Decodes the first frame of a video File into a PNG blob, used as the `frame0.png` thumbnail for a
 * video saved straight into the project filestore. Add-to-scene reads that thumbnail for the aspect
 * ratio (FsVideo.getAspectRatio), and the runtime uses it as a poster. Resolves null on failure.
 */
const generateVideoThumbnail = (file: File): Promise<Blob | null> => {
    return new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.muted = true;
        video.preload = 'auto';
        let settled = false;
        const finish = (blob: Blob | null) => {
            if (settled) return;
            settled = true;
            URL.revokeObjectURL(objectUrl);
            resolve(blob);
        };
        video.onloadeddata = () => {
            // Nudge past frame 0 so a decoded frame is guaranteed before we draw.
            try { video.currentTime = Math.min(0.1, video.duration || 0.1); } catch { finish(null); }
        };
        video.onseeked = () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx || canvas.width === 0 || canvas.height === 0) return finish(null);
            ctx.drawImage(video, 0, 0);
            canvas.toBlob((blob) => finish(blob), 'image/png');
        };
        video.onerror = () => finish(null);
        video.src = objectUrl;
    });
};

export class Filestore {
    private _cds: CDSClient | undefined
    private _jwt: string | undefined
    private _projectId: string | undefined
    private _filesById: {[fileId: string]: FsFile} = {}
    private _blobsByObjectUrl: {[objectUrl: string]: Promise<Blob>} = {}
    // Keyed by whatever the caller passed getBlobUrl: a file id from fileToExternalFile, an object
    // URL from the Fs* getUrl() methods. Two keyspaces in one map, hence the deliberately vague
    // name — a UUID never collides with an https:// URL, but nothing here guarantees that.
    private _blobUrlsByKey: {[key: string]: string} = {}
    private _externalFilesById: { [fileId: string]: FsExternalFile } = {}
    private _cacheLatestAudio: { date: number, files: FsExternalFile[] } | undefined
    private _cache360Files: { date: number, files: FsExternalFile[] } | undefined

    constructor() {/**/}

    async init(cds: CDSClient, projectId: string) {
        this._cds = cds
        this._projectId = projectId
        const req = await this._cds.request(`${this._cds.getProjectUrl()}/filestore/jwt/`)
        let response: undefined | { token: string }
        // CDS answers 401/403 with a `projectAccessError` body rather than a token. Parsing that as a
        // token would leave `_jwt` undefined anyway, but the status is the only thing that says why.
        if (!req.ok) console.error(`Filestore: Unable to get JWT for project ${projectId}: ${req.status}`)
        else {
            try {
                response = await req.json()
            } catch(err) { console.error(err) }
        }
        this._jwt = response?.token
        this._filesById = {}
        this._blobsByObjectUrl = {}
        this._blobUrlsByKey = {}
        this._externalFilesById = {}
    }

    async importFromZml(zmlFileId: string) {
        const importResponse = await this._requestImportFromZml(zmlFileId)
        if (typeof importResponse === 'undefined') {
            console.error('Unable to import file from ZML:', zmlFileId)
            return
        }

        const fsFile = this._create(importResponse)
        store.dispatch(onAddMediaFile_Cn_Doc({ id: fsFile.id, type: fsFile.type, name: fsFile.name }))
        return fsFile
    }

    load<T extends FsFile>(fileId?: string) {
        if (typeof fileId === 'undefined') throw `Filestore: Unable to load undefined file ID`
        if (typeof this._filesById[fileId] !== 'undefined') return this._filesById[fileId] as T
        const filesById = store.getState().contentReducer.contentDoc.mediaFilesById
        const mediaFile = filesById[fileId]
        if (typeof mediaFile === 'undefined') throw `Filestore: File ${fileId} not found in content doc`
        this._filesById[fileId] = this._create<T>(mediaFile)
        return this._filesById[fileId] as T
    }

    private _create<T extends FsFile>(data: IFsFileData): T {
        if (typeof this._projectId === 'undefined') throw 'Filestore: Project ID is undefined'
        switch (data.type) {
            case IFsFileType.Image:
                return (new FsImage(data.id, this._projectId, data.name) as T)
            case IFsFileType.Model3d:
                return (new FsModel3D(data.id, this._projectId, data.name) as T)
            case IFsFileType.Video:
                return (new FsVideo(data.id, this._projectId, data.name) as T)
            case IFsFileType.Audio:
                return (new FsAudio(data.id, this._projectId, data.name) as T)
            case IFsFileType.Image360:
                return (new FsImage360(data.id, this._projectId, data.name) as T)
            case IFsFileType.Video360:
                return (new FsVideo360(data.id, this._projectId, data.name) as T)
            case IFsFileType.ImageTarget:
                return (new FsImageTarget(data.id, this._projectId, data.name) as T)
            default:
                throw `Filestore: Unknown media file type ${data.type}`
        }
    }

    /** Downloads one filestore object (`proj-<id>/<fileId>/<objectKey>`) with the project JWT. */
    async getObjectBlob(objectUrl: string): Promise<Blob> {
        // Cache the in-flight Promise (not the resolved Blob) so concurrent callers for the same
        // url share a single fetch. Without this, N parallel callers each see a cache miss and
        // fire their own request before any of them settle. On failure the cache entry is dropped
        // so the next caller can retry.
        if (typeof this._blobsByObjectUrl[objectUrl] !== 'undefined') return this._blobsByObjectUrl[objectUrl]
        if (typeof this._jwt === 'undefined') throw 'Filestore: JWT is undefined'
        const jwt = this._jwt
        const promise = (async () => {
            const req = await fetch(objectUrl, { headers: [['X-Authorization', jwt]] })
            if (!req.ok) throw new Error(`Filestore: Unable to download file ${req.status} ${objectUrl}`)
            return req.blob()
        })()
        this._blobsByObjectUrl[objectUrl] = promise
        promise.catch(() => { delete this._blobsByObjectUrl[objectUrl] })
        return promise
    }

    /** An already-cached object blob promise, or undefined. Does not trigger a fetch. */
    getCachedObjectBlob(objectUrl: string): Promise<Blob> | undefined {
        return this._blobsByObjectUrl[objectUrl]
    }

    /**
     * Wraps a blob in a `blob:` URL for the DOM to consume, memoised per key so repeated calls hand
     * back the same URL instead of leaking a new one each time. Nothing to do with a filestore
     * object URL — see `_blobUrlsByKey` for what a key can be.
     */
    getBlobUrl(key: string, blob: Blob | File) {
        if (typeof this._blobUrlsByKey[key] !== 'undefined') return this._blobUrlsByKey[key]
        if (blob.size === 0 || blob.type === '') console.warn('Filestore: Empty file', key)
        this._blobUrlsByKey[key] = URL.createObjectURL(blob)
        return this._blobUrlsByKey[key]
    }

    private async _requestImportFromZml(zmlFileId: string) {
        if (typeof this._cds === 'undefined') throw 'Filestore: CDS is undefined'
        const req = await this._cds.request(`${this._cds.getProjectUrl()}/filestore/zml-import/${zmlFileId}/`)
        // The endpoint checks project access before importing and answers 401/403 with an error body
        // ({ error, code }). That body parses as JSON quite happily, so without the status check it
        // would reach _create() and throw "Unknown media file type undefined" instead of saying why.
        if (!req.ok) {
            console.error(`Filestore: ZML import of ${zmlFileId} failed: ${req.status}`)
            return undefined
        }
        let response: undefined | IFsFileData
        try {
            response = await req.json()
        } catch(err) {/**/}
        if (typeof response?.id === 'undefined') {
            console.error(`Filestore: ZML import of ${zmlFileId} returned no file`)
            return undefined
        }
        return response
    }

    async latestAudioFromZml(limit = 5) {
        if (typeof this._cacheLatestAudio === 'undefined' || Date.now() - this._cacheLatestAudio.date > 10000) {
            const workspaceId = store.getState().userReducer.project.accountId
            const resp = await zwClient.zml.search<IFileType.Audio>({ workspaceId, fileTypes: [IFileType.Audio], page: 1, limit })
            this._cacheLatestAudio = { date: Date.now(), files: resp.results.map(zmlToExternalFile) }
        }
        return this._cacheLatestAudio.files
    }

    clearAudioCache() {
        this._cacheLatestAudio = undefined
    }

    async latest360FilesFromZml(limit = 5) {
        if (typeof this._cache360Files === 'undefined' || Date.now() - this._cache360Files.date > 10000) {
            const workspaceId = store.getState().userReducer.project.accountId
            const resp = await zwClient.zml.search<IFileType.Image360 | IFileType.Video360>({ workspaceId, fileTypes: [IFileType.Image360, IFileType.Video360], page: 1, limit })
            this._cache360Files = { date: Date.now(), files: resp.results.map(zmlToExternalFile) }
        }
        return this._cache360Files.files
    }

    clear360FilesCache() {
        this._cache360Files = undefined
    }

    async saveImageTarget(imageFile: File, zptBinary: ArrayBuffer, name: string) {
        if (typeof this._projectId === 'undefined') throw 'Filestore: Project ID is undefined'
        if (typeof this._jwt === 'undefined') throw 'Filestore: JWT is undefined'

        const zptFileHeader = new Uint8Array(32)
        zptFileHeader.set(new TextEncoder().encode(`ZPT+IMG${zptBinary.byteLength};${imageFile.type}`))
        const concatZpt = new Uint8Array(zptFileHeader.byteLength + zptBinary.byteLength + imageFile.size)
        concatZpt.set(zptFileHeader, 0)
        const zptByteArray = new Uint8Array(zptBinary)
        concatZpt.set(zptByteArray, zptFileHeader.byteLength)
        concatZpt.set(new Uint8Array(await imageFile.arrayBuffer()), zptFileHeader.byteLength + zptByteArray.byteLength)

        const fileId = newFsFileId(IFsFileType.ImageTarget)
        const baseUrl = `${FILESTORE_URL}/proj-${this._projectId}/${fileId}`;

        await Promise.all([
            this._putObject(this._buildObjectUrl(baseUrl, 'img'), imageFile, imageFile.type || 'application/octet-stream'),
            this._putObject(this._buildObjectUrl(baseUrl, 'zpt'), concatZpt, 'application/octet-stream')
        ])

        this._blobsByObjectUrl[this._buildObjectUrl(baseUrl, 'img')] = Promise.resolve(imageFile)
        store.dispatch(onAddMediaFile_Cn_Doc({ id: fileId, type: IFsFileType.ImageTarget, name }))
        return new FsImageTarget(fileId, this._projectId, name)
    }

    /**
     * The media-library registry: FsExternalFile wrappers held in memory for the lifetime of the
     * session. Nothing here reaches the filestore — `load`/`saveProjectFile` are the pair that do.
     */
    getExternalFile(fileId: string) {
        return this._externalFilesById[fileId]
    }

    registerExternalFile(fileId: string, file: FsExternalFile) {
        this._externalFilesById[fileId] = file
    }

    /** Builds a filestore object URL the same way the Fs* file classes do, so cache keys line up. */
    private _buildObjectUrl(baseUrl: string, objectKey: string) {
        const url = new URL(baseUrl)
        url.pathname = `${url.pathname}/${objectKey}`
        return url.toString()
    }

    /**
     * PUTs a single object into the project filestore. Throws on a non-2xx rather than letting the
     * caller carry on: an expired JWT answers 401 and, unchecked, the caller would still record the
     * file in the content doc and leave a reference to bytes that were never stored.
     */
    private async _putObject(objectUrl: string, body: BodyInit, contentType: string) {
        if (typeof this._jwt === 'undefined') throw 'Filestore: JWT is undefined'
        const res = await fetch(objectUrl, {
            method: 'PUT',
            headers: { 'X-Authorization': this._jwt, 'Content-Type': contentType },
            body
        })
        if (!res.ok) throw new Error(`Filestore: Unable to upload file ${res.status} ${objectUrl}`)
        return res
    }

    /**
     * Saves a media File straight into the project filestore (`proj-<id>/<fileId>/<objectKey>`) and
     * returns the resulting FsFile, registered in the content doc. Used for external media-library
     * assets so their bytes live in the designer project instead of ZML/workspace storage.
     *
     * The uploaded blob is seeded into the blob cache so the editor renders without a round-trip
     * (mirrors saveImageTarget). For video, a `frame0.png` thumbnail is generated and stored too, as
     * add-to-scene reads it for the aspect ratio.
     */
    async saveProjectFile(fileId: string, file: File, type: IFsFileType, name: string): Promise<FsFile> {
        if (typeof this._projectId === 'undefined') throw 'Filestore: Project ID is undefined'
        if (typeof this._jwt === 'undefined') throw 'Filestore: JWT is undefined'

        const objectKey = PROJECT_FILE_KEY_BY_TYPE[type]
        if (typeof objectKey === 'undefined') throw `Filestore: Unsupported project file type ${type}`

        const baseUrl = `${FILESTORE_URL}/proj-${this._projectId}/${fileId}`
        const objectUrl = this._buildObjectUrl(baseUrl, objectKey)

        await this._putObject(objectUrl, file, CONTENT_TYPE_BY_KEY[objectKey] || file.type || 'application/octet-stream')
        this._blobsByObjectUrl[objectUrl] = Promise.resolve(file)

        if (type === IFsFileType.Video) {
            const thumbnail = await generateVideoThumbnail(file)
            if (thumbnail) {
                const thumbUrl = this._buildObjectUrl(baseUrl, 'frame0.png')
                await this._putObject(thumbUrl, thumbnail, 'image/png')
                // Seed the cache so FsVideo.getThumbnailBlob (used for the aspect ratio) resolves without
                // a round-trip, rather than waiting on CloudFront to serve back what we just wrote.
                this._blobsByObjectUrl[thumbUrl] = Promise.resolve(thumbnail)
            }
        }

        store.dispatch(onAddMediaFile_Cn_Doc({ id: fileId, type, name }))
        return this.load(fileId)
    }
}

// Singleton
export const filestore = new Filestore()
